import { Injectable } from '@angular/core';

import { AuthService, ConfigService } from '../../../../core';
import { isConfigEmpresaVersaoIntegracaoErp1 } from '../../../configuracoes/app/empresa';
import { ConfigCliente } from '../../../configuracoes/app/cliente';
import { isClienteCadastroBaixado, isClienteCadastroCliente, isClienteCadastroProspect } from '../cadastro';
import { isClienteSituacaoBaixado, isClienteSituacaoBloqueado, isClienteSituacaoCongelado, isClienteSituacaoPreBaixado } from '../situacao';

/** Service que valida o acessos a funcionalidades referentes aos clientes */
@Injectable({
    providedIn: 'root'
})
export class ClienteAuthGuardService {

    constructor(private authService: AuthService,
                private configService: ConfigService) { }

    /** Verifica se o usuário pode editar clientes */
    canEdit(editMode: boolean): boolean {
        // Se estiver inserindo, então pode "editar"
        if (!editMode) {
            return true;
        }

        // Somente se o usuário tiver permissão para editar clientes
        return this.authService.podeEditarCliente();
    }

    /**
     * Verifica se pode editar os segmentos do cliente.
     * Exige `podeEditarCliente` E a permissão extra `podeEditarClienteSegmentos`:
     * segmento é dado sensível (impacta indicadores, campanhas e classificação),
     * então só edita segmentos quem já pode editar o cadastro do cliente e ainda
     * possui a permissão específica para segmentos.
     */
    canEditClienteSegmentos(editMode: boolean): boolean {
        // Se estiver inserindo, então pode "editar" os segmentos
        if (!editMode) {
            return true;
        }

        // Exige a permissão de editar o cadastro do cliente E a permissão extra de segmentos
        return this.authService.podeEditarCliente() && this.authService.podeEditarClienteSegmentos();
    }

    /** Verifica se pode realizar edições/alterações nos dados de ficha cadastral do cliente */
    canEditFichaCadastral(editMode: boolean, entity: {clientePrimario: boolean, cadastroCompleto: boolean, integradoErp: boolean, idIntegracaoErp: number}): boolean {
        // Se estiver inserindo, então pode "editar" a ficha cadastral
        if (!editMode) {
            return true;
        }

        // Se não puder editar o cliente, então consequentemente não pode editar a ficha cadastral
        if (!this.canEdit(editMode)) {
            return false;
        }

        // Cliente não informado
        if (!entity) {
            return false;
        }

        // Se não for um cliente primário então não pode editar a ficha cadastral
        if (!entity.clientePrimario) {
            return false;
        }

        // Se o cadastro ainda não estiver completo, então pode editar a ficha cadastral
        if (!entity.cadastroCompleto) {
            return true;
        }

        // Se ainda não estiver integrado com o ERP, então pode editar a ficha cadastral
        if (isConfigEmpresaVersaoIntegracaoErp1(this.configService.getEmpresa())) {
            if (!entity.integradoErp) {
                return false;
            }
        } else {
            if (!entity.idIntegracaoErp) {
                return true;
            }
        }

        return this.configService?.getCliente()?.permiteAtualizarFichaCadastral;
    }

    /** Verifica se pode editar os dados de origem do cliente */
    canEditOrigem(editMode: boolean, origem: number): boolean {
        // Somente quando for um novo cliente ou ainda não possuir a origem definida
        return !editMode || !origem
    }

    /** Verifica se pode realizar edições/alterações no tipo de pagamento */
    canEditTipoPagamento(editMode: boolean, entity: {clientePrimario: boolean, cadastroCompleto: boolean, integradoErp: boolean, idIntegracaoErp: number}): boolean {
        // Se não puder editar a ficha cadastral do cliente então consequentemente não pode editar o tipo de pagamento
        if (!this.canEditFichaCadastral(editMode, entity)) {
            return false;
        }
        // Se estiver inserindo um novo cliente e estiver configurado que não permite editar o tipo de pagamento padrão
        if (!editMode && !this.configService?.getCliente().permiteEditarTipoPagamentoPadrao) {
            return false;
        }

        return true;
    }

    /** Verifica se pode editar a moeda do cliente */
    canEditMoeda(editMode: boolean, entity: {clientePrimario: boolean, cadastroCompleto: boolean, integradoErp: boolean, idIntegracaoErp: number}): boolean {
        // Se não puder editar a ficha cadastral completa do cliente, então não pode editar a moeda
        if (!this.canEditFichaCadastral(editMode, entity)) {
            return false;
        }

        // Somente se for uma empresa exportadora poderá editar a moeda, pois poderá ter clientes do exterior
        return this.configService?.getEmpresa()?.exportadora ?? false;
    }

    /** Verifica se o pode ser inserido um negócio para o prospect/cliente */
    canInsertNegocio(cadastro: number): boolean {
        // Somente se o cadastro for prospect ou cliente
        return isClienteCadastroProspect(cadastro) || isClienteCadastroCliente(cadastro);
    }

    /** Verifica se pode congelar o cliente */
    canCongelar(cadastro: number, situacao: number): boolean {
        // Se o cliente estiver bloqueado não pode congelar
        if (isClienteSituacaoBloqueado(situacao)){
            return false;
        }
        // Somente se for um cliente ainda não congelado
        return isClienteCadastroCliente(cadastro) && !isClienteSituacaoCongelado(situacao);
    }

    /** Verifica se pode descongelar o cliente */
    canDescongelar(cadastro: number, situacao: number): boolean {
        // Somente se for um cliente congelado
        return isClienteCadastroCliente(cadastro) && isClienteSituacaoCongelado(situacao);
    }

    /** Verifica se pode realizar a pré-baixa do cliente */
    canPreBaixar(cadastro: number): boolean {
        // Se o cadastro for igual a Baixado não pode pré-baixar
        if (isClienteCadastroBaixado(cadastro)) {
            return false;
        }

        return true;
    }

    /** Verifica se pode editar a pré-baixa do cliente */
    canEditPreBaixa(cadastro: number, situacao: number): boolean {
        // Somente pode editar a pré-baixa se o cliente estiver pré-baixado
        return isClienteCadastroBaixado(cadastro) && isClienteSituacaoPreBaixado(situacao);
    }

    /** Verifica se pode realizar o cancelamento da pré-baixa do cliente */
    canCancelarPreBaixa(cadastro: number, situacao: number): boolean {
        // Somente pode cancelar a pré-baixa se o cliente estiver pré-baixado
        return isClienteCadastroBaixado(cadastro) && isClienteSituacaoPreBaixado(situacao);
    }

    /** Verifica se pode realizar a baixa do cliente */
    canBaixar(cadastro: number, situacao: number, isAdministrador: boolean, isAtendente: boolean, isFinanceiro: boolean): boolean {
        // Somente pode baixar se o cliente estiver pré-baixado e se o usuário for um adminitrador ou atendente ou do financeiro
        return isClienteCadastroBaixado(cadastro) && isClienteSituacaoPreBaixado(situacao) && (isAdministrador || isAtendente || isFinanceiro);
    }

    /** Verifica se pode editar a baixa do cliente */
    canEditBaixa(cadastro: number, situacao: number, isAdministrador: boolean, isAtendente: boolean, isFinanceiro: boolean): boolean {
        // Somente editar a baixa se o cliente já estiver baixado e se o usuário for um adminitrador ou atendente ou do financeiro
        return isClienteCadastroBaixado(cadastro) && isClienteSituacaoBaixado(situacao) && (isAdministrador || isAtendente || isFinanceiro);
    }

    /** Verifica se pode realizar o cancelamento da baixa do cliente */
    canCancelarBaixa(cadastro: number, situacao: number, isAdministrador: boolean, isAtendente: boolean, isFinanceiro: boolean): boolean {
        // Somente pode cancelar a baixa se o cliente estiver baixado e se o usuário for um adminitrador ou atendente ou do financeiro
        return isClienteCadastroBaixado(cadastro) && isClienteSituacaoBaixado(situacao) && (isAdministrador || isAtendente || isFinanceiro);
    }

    /** Verifica se pode realizar edições/alterações no cliente */
    canEditTipoFiscal(editMode: boolean): boolean {
        // Se estiver inserindo, então pode "editar"
        if (!editMode) {
            return true;
        }

        // Se não puder editar o cliente, então consequentemente não pode editar o tipo fiscal
        if (!this.canEdit(editMode)) {
            return false;
        }

        const configCliente = this.configService.getCliente()
        // Sem config não pode editar
        if (!configCliente) {
            return false;
        }
        // Se não estiver configurado para exibir o tipo fiscal
        if (!configCliente.exibirTipoFiscal) {
            return false;
        }

        // Somente se tiver permissão para edição
        return configCliente.podeEditarTipoFiscal;
    }
}
